import React, { useMemo } from 'react';
import { css } from '@tanium/react-emotion';
import { Icon } from '@tanium/react-graphics';
import { Stack } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { ThemeDefinition } from '@tanium/react-theme-definition';

export interface Props {
  /**
   * Color to pass to the icon.
   */
  iconColor?: ThemeDefinition;
  /**
   * An icon to show before text.
   */
  leadingIcon?: Icon;
  /**
   * Whether the component should use `inline-flex` styling (instead of `flex`). Defaults to
   * `false`.
   */
  inline?: boolean;
  /**
   * The text to show.
   */
  children: React.ReactNode;
  /**
   * aria-label to attach to icon;
   */
  label?: string;
}

const blockStyle = css({ display: 'block' });

/**
 * A component for rendering text next to a vertically centered icon.
 */
export const TextWithIcon = ({
  iconColor,
  leadingIcon: LeadingIcon,
  inline,
  children,
  label,
}: Props) => {
  const theme = useTheme();
  const generatedIconColor = useMemo(
    () => (iconColor ? iconColor(theme) : undefined),
    [iconColor, theme],
  );
  return (
    <Stack itemSpacing={1} inline={inline} alignItems="center">
      {LeadingIcon && (
        <LeadingIcon
          height={16}
          width={16}
          className={blockStyle}
          color={generatedIconColor}
          aria-label={label}
        />
      )}
      {children}
    </Stack>
  );
};
